import axios from 'axios'
import { API_SERVER } from '@/config/Site.js'
import { isFunction, isString } from '@abw/badger-utils'
import { toaster } from './Toast.js'

// base service
export const APIService = axios.create({
  baseURL:          API_SERVER,
  withCredentials:  true,
  responseType:     'json',
})

// GET and POST factory functions expect URL and optional toast options
// e.g.
//  const myAPICall = POST('/example/url')
//  myAPICall({ foo: 123 })
//
// Default toast options are to show toasts for both success and errors.
// See ./Toast.js for other presets, e.g. ToastSuccess, ToastNone

export const GET = (url, defaultOpts) =>
  (params, opts=defaultOpts) => {
    return responder(
      APIService.get(
        isFunction(url)
          ? url(params)
          : url,
        params
      ),
      opts
    )
  }

export const POST = (url, defaultOpts) => {
  return (params, opts=defaultOpts) => {
    return responder(
      APIService.post(
        isFunction(url)
          ? url(params)
          : url,
        params
      ), opts
    )
  }
}

export const PUT = (url, defaultOpts) => {
  return (params, opts=defaultOpts) => {
    return responder(
      APIService.put(
        isFunction(url)
          ? url(params)
          : url,
        params
      ), opts
    )
  }
}

export const DELETE = (url, defaultOpts) => {
  return (params, opts=defaultOpts) => {
    return responder(
      APIService.delete(
        isFunction(url)
          ? url(params)
          : url,
        params
      ), opts
    )
  }
}

// Special case that create POST request using FormData, which can handle
// multi-part forms.
export const POSTFORM = (url, defaultOpts) => {
  return (params, opts=defaultOpts) => {
    return responder(
      APIService.post(
        isFunction(url)
          ? url(params)
          : url,
        PostParams(params),
      ), opts
    )
  }
}

export const PostParams = (params) => {
  let body = new FormData()
  if (params) {
    Object.keys(params).forEach(
      key => body.append(key, params[key])
    )
  }
  return body
}


// The responder() function handles the response from the API call, extracts
// data from the response and forwards it to the toast maker.
export const responder = (request, opts) => {
  return request
    .then(responseData)
    .catch(errorData)
    .then( response => toaster(response, opts) )
}

// Munger to extract response data and return it in a standard format so
// it's easier to grok what happened.
export const responseData = response => {
  const data = isString(response.data)
    ? { message: response.data }
    : response.data

  const ok = responseStatusIsOK(response) && ! data.error
  if (! ok) {
    // Most of the API endpoints return a message item, but if it was an
    // error response then we copy it into data.error so that the
    // badger-form handler can display it as a form error.
    // e.g. "Invalid email address or password"
    data.error ||= data.message
  }

  // standard result format for both success and errors
  return {
    ok:       responseStatusIsOK(response) && ! data.error,
    response: response,
    status:   response.status,
    data:     data,
    message:  data.message || data.success || data.error,
    result:   data.status,
  }
}

// Munger to return error data.  Hopefully we got a JSON response from the
// server, but it might also be a network error reported by axios.
export const errorData = error => {
  return error.response
    ? responseData(error.response)
    : {
        ok:      false,
        status:  999,
        message: error,
        result:  'error',
      }
}

// Did we win big or fail hard?
export const responseStatusIsOK = response =>
  response.status >= 200 && response.status <= 299

